import { Axial } from './HexCoordinates';

/**
 * Terrain, fetched from the server.
 *
 * Chunks arrive as binary over plain HTTP rather than down a socket. Terrain is
 * static: it caches in the browser, a dropped connection costs one retry rather
 * than a resync, and the first frame does not wait on a handshake. The realtime
 * channel, when it exists, carries what changes — edits, entities, players.
 *
 * The wire format is described in `crates/server/src/world.rs`; this is the
 * other half of that contract and refuses anything it does not recognise.
 */

const MAGIC = 'WGCB';
const VERSION = 5;
const HEADER_BYTES = 16;
const CHUNK_HEADER_BYTES = 8;

/** One latitude band of the prevailing winds, as the server describes it. */
export interface WindBand {
  /** Up to this distance from the equator — 0 at the equator, 1 at a pole. */
  until: number;
  /** Which way the air moves along x: −1 out of the east, +1 out of the west. */
  direction: number;
}

/**
 * The winds, sent rather than reimplemented.
 *
 * These bands decide the rain shadow of the whole map, so a copy of their
 * thresholds on this side would keep blowing the old way the day somebody
 * widens the tropics. Optional, because a server older than this sends none —
 * and clouds that all drift one way are a smaller loss than a client that
 * refuses to start.
 */
export interface WindInfo {
  latitudeSpan: number;
  bands: WindBand[];
}

export interface WorldInfo {
  id: string;
  wind?: WindInfo;
  chunkRadius: number;
  latticeRadius: number;
  maxElevation: number;
  maxDepth: number;
  chunkCount: number;
  cellsPerChunk: number;
}

export interface RawChunk {
  center: Axial;
  elevations: Int16Array;
  /** Biome id per cell, or null for a world saved before biomes existed. */
  biomes: Uint8Array | null;
  /** River edge mask per cell, or null for a world saved before rivers existed. */
  rivers: Uint8Array | null;
  /**
   * Lake surface per cell in levels, `NO_LAKE` off a lake, or null for a world
   * saved before lakes had a surface of their own.
   */
  lakes: Int16Array | null;
  /** Woodland richness per cell, zero for none. */
  forest: Uint8Array | null;
  /** Stone richness per cell, zero for none. */
  stone: Uint8Array | null;
  /** Shoal richness per cell, zero for none. Only ever on water. */
  fish: Uint8Array | null;
}

/** The lake layer's way of saying there is no lake here. */
export const NO_LAKE = -32768;

/** How a climate reads once it is known whether trees grew on it. */
export interface Wooded {
  label: string;
  color: string;
}

export interface BiomeInfo {
  /** The byte in the biome layer. */
  id: number;
  label: string;
  /** CSS hex, as the server writes it. */
  color: string;
  /**
   * Czy rośnie tu darń — czy teren ma dostać ziarno trawy.
   *
   * Ze słownika, nie z listy po tej stronie: to jest fakt o klimacie, a lista
   * biomów wypisana w kliencie rozjechałaby się przy pierwszym nowym.
   */
  grass?: boolean;
  /**
   * The wooded reading, absent for the ground biomes.
   *
   * A biome is a **climate**, not a plant: temperate ground is a meadow bare
   * and a wood with trees on it. Which is shown is decided by the forest layer.
   */
  wooded?: Wooded;
}

/** The dictionary as the server keeps it, keyed `BIO_…`. */
export interface Biomes {
  version: number;
  biomes: Record<string, BiomeInfo>;
}

export class WorldSource {
  constructor(private readonly api: string) {}

  /**
   * The biome legend.
   *
   * Fetched rather than hard-coded, for the same reason the layer names travel
   * with the preview: the ids are the wire format, and a copy of the list on
   * this side would mislabel every biome the day one is added.
   */
  async biomes(): Promise<Map<string, BiomeInfo>> {
    const response = await fetch(`${this.api}/api/world/biomes`);
    if (!response.ok) {
      throw new Error(`słownik biomów: ${response.status}`);
    }
    const dictionary = (await response.json()) as Biomes;
    return new Map(Object.entries(dictionary.biomes));
  }

  async info(): Promise<WorldInfo> {
    const response = await fetch(`${this.api}/api/world/info`);
    if (!response.ok) {
      throw new Error(
        `świat niedostępny: ${response.status} ${await response.text()}`
      );
    }
    return (await response.json()) as WorldInfo;
  }

  /**
   * Chunks around a cell, minus the ones already held.
   *
   * `have` is sent because moving the camera one chunk sideways leaves most of
   * the neighbourhood exactly where it was — asking for it again would be the
   * bulk of the traffic.
   */
  async chunks(focus: Axial, radius: number, have: Axial[]): Promise<RawChunk[]> {
    const query = new URLSearchParams({
      q: String(focus.q),
      r: String(focus.r),
      radius: String(radius),
    });
    if (have.length > 0) {
      query.set('have', have.map((c) => `${c.q},${c.r}`).join(';'));
    }

    const response = await fetch(`${this.api}/api/world/chunks?${query}`);
    if (!response.ok) {
      throw new Error(
        `chunki: ${response.status} ${await response.text()}`
      );
    }

    return decodeChunks(await response.arrayBuffer());
  }
}

export function decodeChunks(buffer: ArrayBuffer): RawChunk[] {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);

  const magic = String.fromCharCode(...bytes.subarray(0, 4));
  if (magic !== MAGIC) {
    throw new Error(`to nie jest paczka chunków (magic "${magic}")`);
  }

  const version = view.getUint16(4, true);
  if (version !== VERSION) {
    throw new Error(`wersja formatu ${version}, obsługiwana ${VERSION}`);
  }

  const cells = view.getUint32(8, true);
  const count = view.getUint16(12, true);
  const layers = view.getUint8(14);

  // Layers beyond the first follow the elevations. Counted rather than assumed,
  // so a world saved before a layer existed still streams and the client draws
  // what is there. Biomes and rivers are a byte per cell; a lake surface is a
  // height, so it is two.
  const withBiomes = layers >= 2;
  const withRivers = layers >= 3;
  const withLakes = layers >= 4;
  // Woodland, stone and shoals arrive together or not at all: the server sends
  // the resource block only for a world that has all three files.
  const withResources = layers >= 7;
  const extra =
    (withBiomes ? 1 : 0) +
    (withRivers ? 1 : 0) +
    (withLakes ? 2 : 0) +
    (withResources ? 3 : 0);
  const stride = CHUNK_HEADER_BYTES + cells * 2 + cells * extra;
  const expected = HEADER_BYTES + count * stride;
  if (bytes.length !== expected) {
    throw new Error(`paczka ma ${bytes.length} B, kontrakt mówi ${expected} B`);
  }

  const chunks: RawChunk[] = [];
  for (let i = 0; i < count; i++) {
    const start = HEADER_BYTES + i * stride;
    const heights = start + CHUNK_HEADER_BYTES;
    const biomes = heights + cells * 2;
    const rivers = biomes + (withBiomes ? cells : 0);
    const lakes = rivers + (withRivers ? cells : 0);
    const forest = lakes + (withLakes ? cells * 2 : 0);
    const stone = forest + (withResources ? cells : 0);
    const fish = stone + (withResources ? cells : 0);

    chunks.push({
      center: {
        q: view.getInt32(start, true),
        r: view.getInt32(start + 4, true),
      },
      // Copies, not views: these outlive the response buffer, and a view would
      // keep the whole batch alive for as long as any chunk of it.
      elevations: new Int16Array(buffer.slice(heights, biomes)),
      biomes: withBiomes ? new Uint8Array(buffer.slice(biomes, rivers)) : null,
      rivers: withRivers
        ? new Uint8Array(buffer.slice(rivers, lakes))
        : null,
      lakes: withLakes ? new Int16Array(buffer.slice(lakes, forest)) : null,
      forest: withResources ? new Uint8Array(buffer.slice(forest, stone)) : null,
      stone: withResources ? new Uint8Array(buffer.slice(stone, fish)) : null,
      fish: withResources ? new Uint8Array(buffer.slice(fish, fish + cells)) : null,
    });
  }

  return chunks;
}
